<script setup>
import SmartBox from '../common/SmartBox.vue';
import SearchInput from './components/SearchInput.vue';
import SearchEmpty from './components/SearchEmpty.vue';
import { HeroCarousel } from '../../utils/HeroCarousel';
import { ref, computed, getCurrentInstance, onMounted, nextTick } from 'vue';

const { proxy } = getCurrentInstance();
const __ = proxy.__;

const config = window.__pt.settings.site;

const ICON_MAP = {
  6: 'icons-globe-outline',
  15: 'icons-map',
};
const DEFAULT_ICON = 'icons-location';
const getIcon = (type) => ICON_MAP[type] || DEFAULT_ICON;
const DESTINATION_TYPE = 6;
const TOUR_TYPE = 15;
const MAX_SEARCH_RESULTS = 10;
const submitting = ref(false);
const submitError = ref('');
const tourSearchBox = ref(null);
const tourMobileInput = ref(null);

const query = ref('');
const results = ref([]);
const selectedItem = ref(null);
const loading = ref(false);
const openSearch = ref(false);
const isMobile = ref(false);
const searched = ref(false);

const hasResults = computed(() => results.value.length > 0);
const showEmpty = computed(() => searched.value && !loading.value && !hasResults.value);

async function onSearch(value, mobile = false) {
  query.value = value;
  selectedItem.value = null;

  if (!value || value.trim().length < 3) {
    results.value = [];
    searched.value = false;
    if (!mobile) openSearch.value = false;
    return;
  }

  loading.value = true;
  try {
    const response = await axios.get(config.endPoints.tourKeywordsUrl, { params: { Query: value } });
    results.value = (Array.isArray(response?.data) ? response.data : []).slice(0, MAX_SEARCH_RESULTS);
  } catch (error) {
    console.log(error);
    results.value = [];
  } finally {
    loading.value = false;
    searched.value = true;
    if (!mobile) {
      openSearch.value = false;
      setTimeout(() => {
        openSearch.value = true;
      }, 10);
    }
  }
}

function onSelect(item) {
  selectedItem.value = item;
  query.value = item.displayName;
  if (tourSearchBox.value && typeof tourSearchBox.value.close === 'function') {
    tourSearchBox.value.close({ restoreFocus: false });
  } else {
    openSearch.value = false;
  }
}

function onOpenTourSearch() {
  if (!isMobile.value) return;

  nextTick(() => {
    tourMobileInput.value?.focus();
  });
}

function submitSearch() {
  if (!selectedItem.value) {
    onSearch(query.value);
    return;
  }

  submitError.value = '';
  submitting.value = true;
  document.getElementById('loader-page')?.classList.remove('d-none');

  const item = selectedItem.value;
  const relatedDestination = item.type === TOUR_TYPE
    ? results.value.find(result =>
      result.type === DESTINATION_TYPE &&
      ((item.keywordDestinationId != null &&
        (result.keywordDestinationId === item.keywordDestinationId ||
          result.keywordMappingId === item.keywordDestinationId)) ||
        (item.keywordMappingId != null && result.keywordMappingId === item.keywordMappingId))
    )
    : item;
  const destinationUri = item.destinationUri ?? item.uri ?? relatedDestination?.uri
    ?? relatedDestination?.keyword ?? item.displayName ?? query.value;

  const params = {
    TourDestinationName: item.displayName || query.value,
    TourDestinationId: item.keywordDestinationId ?? relatedDestination?.keywordDestinationId ?? '',
    TourDestinationUri: slugifyTourDestination(destinationUri),
    TourId: item.type === TOUR_TYPE ? (item.tourId ?? item.id ?? '') : '',
    TourDestinationType: item.type ?? '',
    SourceHost: '',
    SiteType: 'None',
    'Searchbox.PostUrl': '',
    'Searchbox.IsModal': 'False',
    'Searchbox.FormMethod': 'Post',
    WhiteLabelId: '',
  };

  const toursSubmitUrl = config.endPoints.toursSubmitUrl
    || config.endPoints.transferSubmitUrl?.replace(/transferSubmit$/i, 'tourSubmit');

  jsonp(toursSubmitUrl, params, { callbackParam: 'jsoncallback' })
    .then((response) => {
      if (response?.Errors?.length) {
        submitError.value = response.Errors.join(' ');
        document.getElementById('loader-page')?.classList.add('d-none');
        return;
      }
      navigateToResults(response);
    })
    .catch((error) => {
      console.log(error);
      document.getElementById('loader-page')?.classList.add('d-none');
      submitError.value = __('booker.tour_submit_error');
    })
    .finally(() => {
      submitting.value = false;
    });
}

function jsonp(url, params, { callbackParam = 'callback', timeoutMs = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error('tours submit URL is not configured'));
      return;
    }

    const callbackName = `__tourSubmitCallback_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const script = document.createElement('script');
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      script.remove();
    };

    window[callbackName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      cleanup();
      reject(new Error('jsonp request failed'));
    };
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('jsonp request timed out'));
    }, timeoutMs);

    const queryString = new URLSearchParams({ ...params, [callbackParam]: callbackName }).toString();
    script.src = `${url}?${queryString}`;
    document.body.appendChild(script);
  });
}

function slugifyTourDestination(destination) {
  return String(destination || '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function navigateToResults(response) {
  if (!response?.Url) {
    throw new Error('tours submit response does not contain a results URL');
  }

  const formEl = document.createElement('form');
  formEl.setAttribute('method', response.Method === 1 ? 'post' : 'get');
  const resultsUrl = /^https?:\/\//i.test(response.Url)
    ? response.Url
    : `${config.tourSiteUrl.replace(/\/$/, '')}/${response.Url.replace(/^\//, '')}`;
  formEl.setAttribute('action', resultsUrl);

  Object.entries(response.FormData || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', value);
    formEl.appendChild(hiddenField);
  });

  document.body.appendChild(formEl);
  formEl.submit();
}

onMounted(() => {
  new HeroCarousel('.hero');
});
</script>

<template>
  <div class="tour-booker-grid">
    <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.tour')">
    <div class="search__inputs">
      <SmartBox ref="tourSearchBox" :title="__('booker.tour_keyword')" :OpenBox="openSearch" :isHeaderActive="false"
        @open="onOpenTourSearch"
        :hasContent="hasResults || showEmpty" @update:OpenBox="openSearch = $event" @isMobile="isMobile = $event">
        <template #trigger>
          <SearchInput id="tour-keyword" :label="__('booker.tour_keyword')"
            :placeholder="__('booker.tour_keyword_placeholder')" v-model="query" @input="onSearch" :debounce="500"
            :autocomplete="'off'" :spellcheck="false" :selectOnFocus="!isMobile" :activeFocus="!isMobile"
            :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-search font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <div class="searcher">
            <div v-if="isMobile" class="searcher__head">
              <SearchInput ref="tourMobileInput" id="tour-keyword-mobile" :label="__('booker.tour_keyword')"
                :placeholder="__('booker.tour_keyword_placeholder')" v-model="query" @input="onSearch($event, true)"
                :debounce="500" :autocomplete="'off'" :spellcheck="false" :selectOnFocus="true"
                :activeFocus="true">
                <template #icon>
                  <span class="icon font-icons icons-search font-20"></span>
                </template>
              </SearchInput>
            </div>
            <ul v-if="hasResults" class="searcher__list">
              <li v-for="(item, index) in results" :key="`tour-${index}`" class="searcher__item">
                <button class="searcher__action" type="button" @click="onSelect(item)">
                  <span class="searcher__icon font-20" :class="getIcon(item.type)"></span>
                  <div class="searcher__info w-100">
                    <p class="searcher__name" v-html="item.displayNameHtml"></p>
                    <span class="searcher__loc">{{ item.cityName }}<template v-if="item.countryName">, {{
                      item.countryName }}</template></span>
                  </div>
                </button>
              </li>
            </ul>
            <SearchEmpty v-else-if="showEmpty" :title="__('booker.no_results_title')"
              :text="__('booker.no_results_hint', [query])" />
          </div>
        </template>
      </SmartBox>
    </div>

    <div class="search__action">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md font-16 font-500"
        :disabled="loading || submitting">
        {{ __('booker.button') }}
      </button>
      <p v-if="submitError" class="search__error font-14">{{ submitError }}</p>
    </div>
    </form>
  </div>
</template>

<style scoped lang="scss">
.tour-booker-grid {
  width: 100%;

  @media (min-width: 1280px) {
    width: calc(100% * 10 / 12);
    max-width: calc(1252px * 10 / 12);
    margin-inline: auto;
  }
}

.search {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-16);
  border-radius: var(--space-12);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  background-color: var(--bg-base);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: 100%;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    display: grid;
    grid-template-areas: 'inputs action';
    grid-template-columns: minmax(0, 1fr) 160px;
    padding-inline: 0px;
    gap: var(--space-24);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1fr) 160px;
    width: 100%;
    margin: 0px auto;
    padding: var(--space-20);
    border-radius: var(--space-12);
    border: 1px solid var(--border-subtle);
    box-shadow: var(--shadow-300);
  }

  .icon {
    display: flex;
  }

  &__action {
    grid-area: action;

    @media (min-width: 768px) {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__inputs {
    grid-area: inputs;
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    @media (min-width: 768px) {
      :deep(.smartbox__container--desktop),
      :deep(.smartbox__panel),
      :deep(.smartbox__content) {
        width: 100%;
      }
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 1280px) {
      width: 160px;
    }
  }
}

.searcher {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  background-color: var(--bg-base);
  width: 100%;

  @media (min-width: 768px) {
    padding: var(--space-16);
    border-radius: var(--space-16);
    width: 100%;
    max-width: none;
    box-sizing: border-box;
  }

  &__head {
    padding-bottom: var(--space-20);
    border-bottom: 1px solid var(--border-subtle);

    @media (min-width: 768px) {
      display: none;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  &__item {
    width: 100%;
    border-bottom: 1px solid var(--border-subtle);
  }

  &__action {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-8);
    border-radius: var(--space-8);
    gap: var(--space-16);
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--bg-level2);
    }
  }

  &__icon {
    width: 35px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: var(--space-8);
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
  }

  &__name {
    font-weight: 400;
    font-size: 1rem;
    color: var(--text-strong);
    margin: 0;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &__loc {
    font-size: 0.875rem;
    color: var(--text-subtle);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
  }
}
</style>
